import { ABUSE_EMAIL } from "../config";

const UPDATED = "September 23, 2026";

export function Security() {
  return (
    <section className="panel legal">
      <h2>Security model</h2>
      <p className="hint">How LimeShare protects files, and what it cannot protect against. Last updated {UPDATED}.</p>

      <h3>What happens to your file</h3>
      <ol>
        <li>Your browser generates a random 256-bit master key. It never leaves your device except inside the link.</li>
        <li>
          Each file is encrypted in 4 MiB frames with AES-256-GCM using the STREAM construction (the open LSC1 format).
          Truncation, reordering and tampering are detected before a single byte of plaintext is released.
        </li>
        <li>File names, sizes, types and your message are encrypted into a manifest with a separate derived key.</li>
        <li>Encrypted bytes go directly from your browser to object storage. Our API only coordinates and never sees them.</li>
        <li>
          The link contains the key and an access token after the <code>#</code> symbol. Browsers never send that part to any
          server. We store only a SHA-256 hash of the token.
        </li>
        <li>The recipient's browser downloads the ciphertext, verifies every frame, and decrypts it locally.</li>
      </ol>

      <h3>What our servers know</h3>
      <ul>
        <li>Transfer id, creation and expiry time, number of files and their encrypted sizes.</li>
        <li>A hash of the access token; the encrypted manifest (opaque to us).</li>
        <li>A salted, daily-rotating hash of the uploader's IP address for abuse limits. Raw IPs are not stored.</li>
      </ul>
      <p>We do not know file names, contents, types, who you are, or who receives the link.</p>

      <h3>What this does not protect against</h3>
      <ul>
        <li>Anyone who obtains the full link can decrypt the files. Share it over a channel you trust.</li>
        <li>Malware or a compromised browser on the sender's or recipient's device.</li>
        <li>Metadata visible to your network: that you visited limeshare.org and transferred roughly N bytes.</li>
      </ul>

      <h3>Verifiability</h3>
      <p>
        The LSC1 protocol specification, the client-side implementation and cross-implementation test vectors are published as
        open source. Release hashes are published so anyone can verify the code served by this site.
      </p>

      <h3>Reporting</h3>
      <p>
        Security issues: <a href={`mailto:${ABUSE_EMAIL}`}>{ABUSE_EMAIL}</a> (PGP key published on this site). We aim to
        acknowledge reports within 24 hours.
      </p>
    </section>
  );
}

export function Terms() {
  return (
    <section className="panel legal">
      <h2>Terms of Service</h2>
      <p className="hint">Last updated {UPDATED}. By using LimeShare you agree to these terms.</p>

      <h3>1. The service</h3>
      <p>
        LimeShare ("the service", operated by the LimeShare operators, "we") lets you encrypt files in your browser and share
        them through time-limited links. Files are stored only in encrypted form and are deleted automatically according to the
        plan you use. The service is provided as is, without warranty of any kind.
      </p>

      <h3>2. Acceptable use</h3>
      <p>You must not use the service to store or distribute:</p>
      <ul>
        <li>child sexual abuse material or any content sexualizing minors;</li>
        <li>malware, or content intended to compromise systems or defraud people;</li>
        <li>content that infringes copyright or other rights of third parties;</li>
        <li>personal data of others published without their consent;</li>
        <li>content promoting violence, terrorism or hate against protected groups;</li>
        <li>anything illegal in your jurisdiction or in the jurisdiction of the recipient.</li>
      </ul>
      <p>You must be at least 18 years old to use the service.</p>

      <h3>3. Enforcement</h3>
      <p>
        We cannot see file contents. When we receive a report, we act on the reported link: we may block it, delete the encrypted
        objects and restrict the uploader. Reports concerning child sexual abuse material lead to immediate removal and are
        forwarded to the relevant hotlines. Repeat infringers are banned.
      </p>

      <h3>4. Notice and takedown</h3>
      <p>
        Rights holders and anyone else may report a transfer via the "Report abuse" link on every download page or by email to{" "}
        <a href={`mailto:${ABUSE_EMAIL}`}>{ABUSE_EMAIL}</a>. Include the full link (or transfer id), the reason, and your contact
        details. We act on valid notices within 24 hours. Uploaders may submit a counter notice to the same address.
      </p>

      <h3>5. Your responsibility for keys</h3>
      <p>
        The decryption key exists only in the link. If you lose the link, the files cannot be recovered by anyone, including us.
        Anyone holding the link can decrypt the files.
      </p>

      <h3>6. Paid plans</h3>
      <p>
        Paid plans are prepaid periods purchased with cryptocurrency. They do not renew automatically. Files of expired paid plans
        are kept read-only for 14 days, then deleted after a further notice period. Refunds are issued only when the service
        could not be delivered.
      </p>

      <h3>7. Limitation of liability</h3>
      <p>
        To the maximum extent permitted by law, the service is provided without warranties, and we are not liable for any
        indirect, incidental or consequential damages, loss of data, or loss of profits arising from your use of the service.
        Our total liability is limited to the amount you paid us in the preceding three months.
      </p>

      <h3>8. Changes</h3>
      <p>We may update these terms; the date above reflects the latest version. Continued use means acceptance.</p>
    </section>
  );
}

export function Privacy() {
  return (
    <section className="panel legal">
      <h2>Privacy Policy</h2>
      <p className="hint">Last updated {UPDATED}. Short version: we designed the service so that there is almost nothing to collect.</p>

      <h3>Data we process</h3>
      <table>
        <thead>
          <tr>
            <th>Data</th>
            <th>Purpose</th>
            <th>Retention</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Encrypted files and encrypted manifest</td>
            <td>Delivering the transfer</td>
            <td>Until expiry, burn, or deletion (48 h for anonymous transfers)</td>
          </tr>
          <tr>
            <td>Transfer metadata: id, encrypted sizes, timestamps, hash of the access token</td>
            <td>Operating the service</td>
            <td>Until deletion, then purged from our database within 30 days</td>
          </tr>
          <tr>
            <td>Salted daily hash of the IP address</td>
            <td>Rate limiting and abuse prevention</td>
            <td>3 days</td>
          </tr>
          <tr>
            <td>Abuse reports (content you submit, hash of reporter IP)</td>
            <td>Handling reports, legal compliance</td>
            <td>As long as necessary for the case</td>
          </tr>
          <tr>
            <td>Account number and optional email (accounts, when launched)</td>
            <td>Authentication, notifications you opt into</td>
            <td>Until you delete the account</td>
          </tr>
        </tbody>
      </table>

      <h3>Data we do not have</h3>
      <ul>
        <li>Decryption keys, file contents, file names or types.</li>
        <li>Raw IP addresses, precise location, device fingerprints beyond a coarse abuse-prevention hash.</li>
        <li>Advertising identifiers. We run no advertising and no third-party analytics or tracking scripts.</li>
      </ul>

      <h3>Cookies</h3>
      <p>Anonymous use sets no cookies. Accounts will use a strictly necessary session cookie only.</p>

      <h3>Third parties</h3>
      <p>
        Encrypted objects are stored across encrypted edge storage clusters; bot checks use privacy-preserving automated challenges.
        These providers process encrypted data and connection metadata under their own policies. We do not sell or share data with anyone else.
      </p>

      <h3>Legal requests</h3>
      <p>
        We respond only to valid legal process. Because of the design, the information we can provide is limited to the
        metadata listed above. We publish a transparency report twice a year.
      </p>

      <h3>Your rights</h3>
      <p>
        Where data protection law applies to you (for example the GDPR), you may request access to or deletion of data we hold.
        Since transfers are anonymous, requests must reference the transfer id. Contact:{" "}
        <a href={`mailto:${ABUSE_EMAIL}`}>{ABUSE_EMAIL}</a>.
      </p>
    </section>
  );
}
